import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { NovaButton } from '@rds-nova/angular-button';
import { NovaIcon, NovaIconRegistry } from '@rds-nova/angular-icon';
import { NovaProgressSpinner } from '@rds-nova/angular-progress-spinner';
import { filter, map, startWith, withLatestFrom } from 'rxjs/operators';
import {
  NovaTabs,
  NovaTabsItem,
  NovaTabsItemContent,
  NovaTabsList,
} from '@rds-nova/angular-tabs';
import { RonMonitorStore } from './+state/ron-monitor.store';
import { MonitorDatePickerComponent } from './components/monitor-date-picker/monitor-date-picker.component';
import { NotaryGridComponent } from './components/notary-grid/notary-grid.component';
import { SummaryCardsComponent } from './components/summary-cards/summary-cards.component';
import { RonMonitorDataSource } from './models/ron-monitor.data-source';
import {
  BOARD_TIME_ZONE_NOTE,
  formatBoardDate,
  formatBoardShortTime,
  fromBoardWallClock,
  toBoardWallClock,
} from './ron-closing-monitor.utils';
import { BoardDesignationFilter, ClosingPill } from './models/ron-monitor.models';
import { ApiRonMonitorDataSource } from './services/api-ron-monitor.data-source';
import { Features } from '../shared/enums/Features';
import { FeaturesService } from '../shared/services/features.service';

const DESIGNATION_TABS: readonly BoardDesignationFilter[] = ['all', 'tsa', 'preferred'];

@Component({
  selector: 'app-ron-closing-monitor',
  templateUrl: './ron-closing-monitor.component.html',
  styleUrls: ['./ron-closing-monitor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NovaButton,
    NovaIcon,
    NovaIconRegistry,
    NovaProgressSpinner,
    NovaTabs,
    NovaTabsItem,
    NovaTabsItemContent,
    NovaTabsList,
    MonitorDatePickerComponent,
    NotaryGridComponent,
    SummaryCardsComponent,
  ],
  providers: [
    { provide: RonMonitorDataSource, useClass: ApiRonMonitorDataSource },
    RonMonitorStore,
  ],
})
export class RonClosingMonitorComponent {
  private readonly store = inject(RonMonitorStore);
  private readonly toastr = inject(ToastrService);
  private readonly features = inject(FeaturesService);

  readonly timeZoneNote = BOARD_TIME_ZONE_NOTE;
  readonly pickerDate$ = this.store.date$.pipe(map(toBoardWallClock));
  readonly dateLabel$ = this.store.date$.pipe(map(formatBoardDate));
  readonly summary$ = this.store.summary$;
  readonly lanes$ = this.store.lanes$;
  readonly closings$ = this.store.closings$;
  readonly boardTime$ = this.store.lastRefreshedAt$.pipe(
    map((value) => (value ? new Date(value) : null))
  );
  readonly boardTimeLabel$ = this.boardTime$.pipe(
    map((value) => (value ? formatBoardShortTime(value) : null))
  );
  readonly awaitingFirstBoard$ = this.store.awaitingFirstBoard$;
  readonly loadFailed$ = this.store.loadFailed$;
  readonly refreshFailed$ = this.store.refreshFailed$;
  readonly refreshing$ = this.store.manualRefresh$;
  readonly designation$ = this.store.designation$;
  readonly searchTerm$ = this.store.searchTerm$;
  readonly expandedPackageIds$ = this.store.expandedPackageIds$;
  readonly details$ = this.store.details$;
  readonly panelActionsEnabled$ = this.features
    .isFeatureEnabled(Features.RcmPanelActions)
    .pipe(startWith(false));

  constructor() {
    this.store.error$
      .pipe(
        filter((message): message is string => message !== null),
        withLatestFrom(this.store.board$),
        takeUntilDestroyed()
      )
      .subscribe(([, board]) =>
        this.toastr.error(
          board === null
            ? 'Unable to load the closing board'
            : 'Unable to refresh the closing board'
        )
      );
  }

  refresh(): void {
    this.store.refresh();
  }

  onDateChange(pickedWallClock: Date): void {
    this.store.setDate(fromBoardWallClock(pickedWallClock));
  }

  onDesignationChange(value: string): void {
    const designation = DESIGNATION_TABS.find((tab) => tab === value);
    if (designation) {
      this.store.setDesignation(designation);
    }
  }

  onSearch(term: string): void {
    this.store.setSearchTerm(term);
  }

  onPillActivate(closing: ClosingPill): void {
    this.store.toggleExpanded(closing.packageId);
  }

  onPanelRetry(packageId: number): void {
    this.store.retryDetail(packageId);
  }

  onPanelDismiss(packageId: number): void {
    this.store.collapse(packageId);
  }
}
